'use client'

import { useHsChapter } from '@/modules/hs/hooks/useHsCodes'
import { useState, useMemo, useEffect } from 'react'
import { HsPart, HsChapter } from '@/modules/hs/types/hs-response'
import { Network } from 'lucide-react'
import clsx from 'clsx'

export default function HSChapterFilter({
  setSelectedChapters,
  selectedChapters,
}: {
  setSelectedChapters: any
  selectedChapters: number[]
}) {
  const { data: parts, isLoading } = useHsChapter()
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [localSelectedChapters, setLocalSelectedChapters] = useState<number[]>(selectedChapters)

  // Sync local state with prop changes (e.g., when URL changes)
  useEffect(() => {
    setLocalSelectedChapters(selectedChapters)
  }, [selectedChapters])

  // Parse chapter number from numberVN (e.g. "Chương 09" -> 9)
  const parseChapterNumber = (numberVN: string) =>
    parseInt(numberVN?.replace(/\D/g, '') || '0', 10)

  // Group chapters by part
  const groupedChapters = useMemo(() => {
    if (!parts) return {}

    return parts.reduce((acc, part) => {
      const partKey = part.numberVN
      const partName = `${part.numberVN} - ${part.nameVN}`

      acc[partKey] = {
        fullName: partName,
        chapters: [...part.chapters].sort(
          (a, b) => parseChapterNumber(a.numberVN) - parseChapterNumber(b.numberVN)
        ),
      }
      return acc
    }, {} as Record<string, { fullName: string; chapters: HsChapter[] }>)
  }, [parts])

  // Filter by search term
  const filteredGroups = useMemo(() => {
    if (!searchTerm) return groupedChapters

    const lowerSearchTerm = searchTerm.toLowerCase()
    const filtered: Record<
      string,
      { fullName: string; chapters: HsChapter[] }
    > = {}

    parts?.forEach((part: HsPart) => {
      // Filter chapters trong part
      const matchedChapters = part.chapters
        .filter((ch: HsChapter) =>
          ch.nameVN?.toLowerCase().includes(lowerSearchTerm) ||
          ch.numberVN?.toLowerCase().includes(lowerSearchTerm),
        )
        .sort((a, b) => parseChapterNumber(a.numberVN) - parseChapterNumber(b.numberVN))

      // Nếu có chapter match, thêm vào filtered
      if (matchedChapters.length > 0) {
        const partKey = part.numberVN
        const partName = `${part.numberVN} - ${part.nameVN}`

        filtered[partKey] = {
          fullName: partName,
          chapters: matchedChapters,
        }
      }
    })

    return filtered
  }, [parts, searchTerm])

  const handleClearAll = () => {
    setSelectedChapters([])
    setLocalSelectedChapters([])
  }

  const totalChapters =
    Object.values(filteredGroups).reduce(
      (sum, part) => sum + part.chapters.length,
      0,
    ) || 0

  // Get selected chapter names for display
  const selectedChapterNames = useMemo(() => {
    if (!parts || localSelectedChapters.length === 0) return ''

    const allChapters: HsChapter[] = []
    parts.forEach((part) => {
      allChapters.push(...part.chapters)
    })

    const selected = allChapters.filter((ch) => {
      const chapterId = typeof ch.id === 'string' ? parseInt(ch.id, 10) : ch.id
      return localSelectedChapters.includes(chapterId)
    })

    if (selected.length === 0) return ''
    if (selected.length === 1) {
      return `${selected[0].numberVN} - ${selected[0].nameVN}`
    }
    // Show first chapter + count if multiple
    return `${selected[0].numberVN}, ${selected[0].nameVN.split(',')[0]}...`
  }, [parts, localSelectedChapters])

  if (isLoading) {
    return (
      <div className="relative">
        <button className="px-4 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm">
          Đang tải...
        </button>
      </div>
    )
  }

  return (
    <div className="relative max-w-[400px]">
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="hs-filter-button flex items-center gap-2"
        style={{
          width: '100%',
        }}
      >
        <Network size={16} className="shrink-0" />
        <span className="truncate font-bold">
          {selectedChapterNames || 'Tìm theo Phần, Chương'}
          {localSelectedChapters.length > 1 && ` (+${localSelectedChapters.length - 1})`}
        </span>
        <svg
          className={clsx(
            'w-3 h-3 transition-transform shrink-0',
            isOpen && 'rotate-180'
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-[500px] bg-white border border-gray-300 rounded shadow-lg z-50 max-h-[400px] flex flex-col">
          {/* Header */}
          <div className="p-2 border-b border-gray-200">
            <div className="flex items-center gap-1.5 mb-2">
              <input
                type="text"
                placeholder="Tìm kiếm nhanh Phần và Chương"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs font- focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={() => setSearchTerm('')}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <button
                onClick={handleClearAll}
                className="px-2 py-1 border border-gray-300 rounded text-xs hover:bg-gray-50"
              >
                Bỏ chọn tất cả
              </button>
            </div>
            <div className="text-xs text-gray-600">
              Tất cả ({totalChapters} chương)
            </div>
          </div>

          {/* Chapter List */}
          <div className="overflow-y-auto flex-1">
            {Object.entries(filteredGroups).map(([partKey, partData]) => {
              // Tính toán tất cả IDs trong Part này (convert string to number if needed)
              const allPartIds: number[] = partData.chapters.map((ch) =>
                typeof ch.id === 'string' ? parseInt(ch.id, 10) : ch.id
              )

              // Đếm số chapters đã chọn
              const selectedCount = allPartIds.filter((id) =>
                localSelectedChapters.includes(id),
              ).length
              const isAllSelected =
                selectedCount === allPartIds.length && allPartIds.length > 0
              const isPartialSelected =
                selectedCount > 0 && selectedCount < allPartIds.length

              return (
                <div
                  key={partKey}
                  className="border-b border-gray-200 last:border-b-0"
                >
                  {/* Part Header */}
                  <div className="px-3 py-1.5 bg-gray-50 font-semibold text-xs flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      className="w-3.5 h-3.5"
                      checked={isAllSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = isPartialSelected
                      }}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Chọn tất cả chapters trong Part
                          const newSelection = [
                            ...localSelectedChapters,
                            ...allPartIds.filter((id) => !localSelectedChapters.includes(id)),
                          ]
                          setLocalSelectedChapters(newSelection)
                          setSelectedChapters(newSelection)
                        } else {
                          // Bỏ chọn tất cả chapters trong Part
                          const newSelection = localSelectedChapters.filter((id) => !allPartIds.includes(id))
                          setLocalSelectedChapters(newSelection)
                          setSelectedChapters(newSelection)
                        }
                      }}
                    />
                    {partData.fullName}
                  </div>

                  {/* Chapters */}
                  {partData.chapters.map((chapter: HsChapter) => {
                    const chapterId = typeof chapter.id === 'string' ? parseInt(chapter.id, 10) : chapter.id
                    const isChecked = localSelectedChapters.includes(chapterId)
                    return (
                      <label
                        key={chapterId}
                        className={clsx(
                          'px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-start gap-1.5 text-left',
                          isChecked && 'bg-blue-50'
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            const newSelection = localSelectedChapters.includes(chapterId)
                              ? localSelectedChapters.filter((id) => id !== chapterId)
                              : [...localSelectedChapters, chapterId]
                            setLocalSelectedChapters(newSelection)
                            setSelectedChapters(newSelection)
                          }}
                          className="w-3.5 h-3.5 mt-0.5 shrink-0"
                        />
                        <span className="text-xs flex-1">
                          {chapter.numberVN} - {chapter.nameVN}
                        </span>
                      </label>
                    )
                  })}
                </div>
              )
            })}

            {Object.keys(filteredGroups).length === 0 && (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                Không tìm thấy kết quả
              </div>
            )}
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
      )}
    </div>
  )
}
